import { indexOf, isEmpty, orderBy, some, stubTrue } from "lodash/fp";

import type {
  OnboardingStepContext,
  StepId,
  StepStatus,
} from "@app/analytics/onboarding/types";
import ROUTES from "@app/common/routes";

interface StepConfig {
  id: StepId;
  parentId?: StepId;
  title: string;
  route: string;
  isEnabled: (context: OnboardingStepContext) => boolean;
  getInitialStatus: (context: OnboardingStepContext) => StepStatus;
}

// the natural step ordering
const STEP_IDS_IN_ORDER: StepId[] = [
  "workspace",
  "calendar",
  "integrations",
  "wrapUp",
];

const entryPointIsPastStep = (
  entryPoint: OnboardingStepContext["entryPoint"],
  stepId: StepId
) => {
  if (!entryPoint) return false;
  return (
    STEP_IDS_IN_ORDER.indexOf(entryPoint) > STEP_IDS_IN_ORDER.indexOf(stepId)
  );
};

const workspaceInvite: StepConfig = {
  id: "workspace",
  title: "Workspace",
  route: ROUTES.WELCOME_WORKSPACE,
  isEnabled: ({ workspaceInvite }) => !isEmpty(workspaceInvite),
  getInitialStatus: ({ entryPoint, workspaceInvite }) => {
    if (isEmpty(workspaceInvite) || workspaceInvite.status === "pending") {
      return entryPointIsPastStep(entryPoint, "workspace")
        ? "skipped"
        : "pending";
    }

    return workspaceInvite.status === "accepted" ? "complete" : "skipped";
  },
};

const calendar: StepConfig = {
  id: "calendar",
  title: "Calendar",
  route: ROUTES.WELCOME_CALENDAR,
  isEnabled: ({ featureAccess }) => featureAccess.canConnectCalendars,
  getInitialStatus: ({ connectedIntegrations, entryPoint }) => {
    const hasConnectedCalendars = some(
      { category: "calendar" },
      connectedIntegrations
    );
    return hasConnectedCalendars
      ? "complete"
      : entryPointIsPastStep(entryPoint, "calendar")
        ? "skipped"
        : "pending";
  },
};

const integrations: StepConfig = {
  id: "integrations",
  title: "Integrations",
  route: ROUTES.WELCOME_INTEGRATIONS,
  isEnabled: ({ featureAccess }) =>
    featureAccess.canConnectProductivityIntegrations,
  getInitialStatus: ({ connectedIntegrations, entryPoint }) => {
    const hasConnectedIntegrations = some(
      { category: "productivity" },
      connectedIntegrations
    );
    return hasConnectedIntegrations
      ? "complete"
      : entryPointIsPastStep(entryPoint, "integrations")
        ? "skipped"
        : "pending";
  },
};

const wrapUp: StepConfig = {
  id: "wrapUp",
  title: "Wrap Up",
  route: ROUTES.WELCOME_WRAP_UP_NEW,
  isEnabled: stubTrue,
  getInitialStatus: () => "pending",
};

const STEPS = orderBy((step) => indexOf(step.id, STEP_IDS_IN_ORDER), "asc", [
  workspaceInvite,
  calendar,
  integrations,
  wrapUp,
]);

export default STEPS;
